import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "./AuthContext";

/**
 * 3-state ProtectedRoute:
 *   null  → checking (loading)
 *   true  → render children
 *   false → navigate to /login
 *
 * If `location.state.user` is present (from AuthCallback navigation), we skip
 * the /auth/me round-trip to avoid the race condition.
 */
export default function ProtectedRoute({ children, allowRoles }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user || user ? true : null,
  );

  useEffect(() => {
    if (isAuthenticated === true) return;
    // CRITICAL: skip /auth/me if we're mid-callback (handled elsewhere).
    if (window.location.hash?.includes("session_id=")) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/auth/me");
        if (cancelled) return;
        setUser(res.data);
        setIsAuthenticated(true);
      } catch (_e) {
        if (cancelled) return;
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAuthenticated === null) {
    return (
      <main
        data-testid="protected-route-loading"
        className="flex min-h-screen items-center justify-center bg-white"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900" />
      </main>
    );
  }

  if (isAuthenticated === false) return null;

  if (allowRoles && user && !allowRoles.includes(user.role)) {
    return (
      <main
        data-testid="protected-route-forbidden"
        className="flex min-h-screen items-center justify-center bg-white px-6"
      >
        <div className="max-w-md text-center">
          <p className="text-sm uppercase tracking-wide text-zinc-500">
            403 — Forbidden
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-zinc-900">
            Your role can't view this page
          </h1>
        </div>
      </main>
    );
  }

  return children;
}
